import { useEffect, useRef, useState } from "react";

// Lightweight pull-to-refresh for the mobile (mlux) screens, which scroll the
// window/body. When the page is at the top and the user drags down past the
// threshold, onRefresh() is invoked. No external dependency.
const THRESHOLD = 70;

export default function PullToRefresh({ onRefresh, children }) {
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const startY = useRef(0);
    const pulling = useRef(false);
    const pullRef = useRef(0);
    const refreshingRef = useRef(false);
    const cb = useRef(onRefresh);
    cb.current = onRefresh;

    const setP = (v) => { pullRef.current = v; setPull(v); };

    useEffect(() => {
        const onStart = (e) => {
            if (window.scrollY <= 0 && !refreshingRef.current) {
                startY.current = e.touches[0].clientY;
                pulling.current = true;
            } else {
                pulling.current = false;
            }
        };
        const onMove = (e) => {
            if (!pulling.current || refreshingRef.current) return;
            const dy = e.touches[0].clientY - startY.current;
            if (dy > 0 && window.scrollY <= 0) {
                // Resistance so the pull feels natural.
                setP(Math.min(dy * 0.5, 90));
            } else if (dy <= 0) {
                setP(0);
            }
        };
        const onEnd = async () => {
            if (!pulling.current) return;
            pulling.current = false;
            if (pullRef.current >= THRESHOLD && !refreshingRef.current) {
                refreshingRef.current = true;
                setRefreshing(true);
                setP(46);
                try {
                    await cb.current?.();
                } catch (err) {
                    // swallow — the refreshed view handles its own errors
                } finally {
                    refreshingRef.current = false;
                    setRefreshing(false);
                    setP(0);
                }
            } else {
                setP(0);
            }
        };
        document.addEventListener("touchstart", onStart, { passive: true });
        document.addEventListener("touchmove", onMove, { passive: true });
        document.addEventListener("touchend", onEnd);
        return () => {
            document.removeEventListener("touchstart", onStart);
            document.removeEventListener("touchmove", onMove);
            document.removeEventListener("touchend", onEnd);
        };
    }, []);

    const ready = pull >= THRESHOLD;
    return (
        <>
            <div className="mlux-ptr" style={{ height: pull }}>
                {pull > 4 && (
                    <i className={`fas ${refreshing ? "fa-spinner mlux-ptr-spin" : ready ? "fa-arrow-up" : "fa-arrow-down"}`}></i>
                )}
            </div>
            {children}
        </>
    );
}
